import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from 'react-jss';
import { safeRest } from '@wds/utils';
import { IconInfo, IconWarning, IconCheck, IconLock } from '@wds/icons';
import { Slide } from '@wds/motion';
import { CloseButton } from '@wds/internal-components';

import ToastTitle from './ToastTitle';
import ToastBody from './ToastBody';
import ToastFooter from './ToastFooter';

import useStyles from './useStyles';

export const TOAST_COLORS = ['info', 'warning', 'upgrade', 'success'];
const TIMEOUT_DELAY = 3000;

const Toast = ({
  ariaLabel,
  onClose,
  autoClose,
  children,
  dismissible,
  color,
  show,
  ...rest
}) => {
  const timeoutRef = useRef({ timeout: null });
  const [localShow, setLocalShow] = useState(show);

  const theme = useTheme();
  const classes = useStyles({ color, theme });

  const ToastIcon = {
    info: IconInfo,
    warning: IconWarning,
    success: IconCheck,
    upgrade: IconLock,
  }[color];

  const handleClose = () => {
    clearInterval(timeoutRef.current.timeout);
    setLocalShow(false);
    if (onClose) {
      onClose();
    }
  };

  const handleMouseEnter = () => {
    if (autoClose) {
      clearInterval(timeoutRef.current.timeout);
    }
  };

  const handleMouseExit = () => {
    if (autoClose) {
      timeoutRef.current.timeout = setTimeout(
        () => handleClose(),
        TIMEOUT_DELAY
      );
    }
  };

  useEffect(() => {
    if (autoClose && show) {
      timeoutRef.current.timeout = setTimeout(
        () => handleClose(),
        TIMEOUT_DELAY
      );
    } else {
      clearInterval(timeoutRef.current.timeout);
    }
    setLocalShow(show);
  }, [show]);

  return (
    <div className={classes.wrenchToastContainer}>
      <Slide in={localShow} distance="far">
        <div>
          <div
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseExit}
            className={classes.wrenchToast}
            role="alert"
            {...safeRest(rest)}
          >
            <If condition={dismissible}>
              <CloseButton
                onClick={handleClose}
                label={ariaLabel}
                variant="light"
              />
            </If>
            <If condition={color}>
              <div className={classes.wrenchToastIcon}>
                <ToastIcon color={color} title={`${color} icon`} />
              </div>
            </If>
            {children}
          </div>
        </div>
      </Slide>
    </div>
  );
};

Toast.propTypes = {
  /**
   * If true, Toast will auto close after 3 seconds
   */
  autoClose: PropTypes.bool,
  /**
   * Toast is composed of 3 subcomponents:
   * Toast.Title,
   * Toast.Body,
   * Toast.Footer
   */
  children: PropTypes.node.isRequired,
  /**
   * If true, an dismissble X icon will appear on the top right
   */
  dismissible: PropTypes.bool,
  /**
   * onClose event handler
   * function(event: object) => void
   */
  onClose: PropTypes.func,
  /**
   * Color variants for Toast
   */
  color: PropTypes.oneOf(TOAST_COLORS),
  /**
   * Specify custom aria label
   */
  ariaLabel: PropTypes.string,
  /**
   * If true, Toast will be visible in view
   */
  show: PropTypes.bool,
};

Toast.defaultProps = {
  autoClose: true,
  dismissible: false,
  onClose: undefined,
  color: undefined,
  ariaLabel: 'Close Toast',
  show: true,
};

Toast.Title = ToastTitle;
Toast.Body = ToastBody;
Toast.Footer = ToastFooter;

export default Toast;
